import { useEffect, useState } from 'react';
import api, { formatApiError } from '../lib/api';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from '../components/ui/dialog';
import { toast } from 'sonner';
import { Megaphone, Pin, Plus, Trash2, Sparkles, AlertCircle } from 'lucide-react';

export default function Announcements() {
  const { user } = useAuth();
  const canPost = ['admin', 'hr'].includes(user?.role);
  const [list, setList] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ title: '', content: '', priority: 'normal', is_pinned: false });
  const [busy, setBusy] = useState(false);

  const load = async () => {
    const { data } = await api.get('/announcements');
    setList(data);
  };
  useEffect(() => { load(); }, []);

  const submit = async () => {
    if (!form.title || !form.content) return toast.error('Title and content required');
    setBusy(true);
    try {
      await api.post('/announcements', form);
      toast.success('Posted'); setOpen(false);
      setForm({ title: '', content: '', priority: 'normal', is_pinned: false }); load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const del = async (id) => {
    if (!window.confirm('Delete this announcement?')) return;
    try { await api.delete(`/announcements/${id}`); toast.success('Deleted'); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  return (
    <div className="space-y-6" data-testid="announcements-page">
      <div className="flex items-end justify-between flex-wrap gap-4">
        <div>
          <div className="text-xs font-bold uppercase tracking-[0.25em] text-slate-500">Company feed</div>
          <h1 className="font-heading text-3xl sm:text-4xl font-bold tracking-tight text-slate-900 mt-1">Announcements</h1>
        </div>
        {canPost && (
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
              <Button data-testid="new-announcement-button" className="rounded-full bg-[#3D4A8E] hover:bg-[#5C6BC0] text-white px-6 h-11">
                <Plus className="w-4 h-4 mr-2" /> New post
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-lg" data-testid="new-announcement-dialog">
              <DialogHeader><DialogTitle className="font-heading">New announcement</DialogTitle></DialogHeader>
              <div className="space-y-3 mt-2">
                <div>
                  <Label className="text-xs font-semibold text-slate-600">Title</Label>
                  <Input data-testid="ann-title" value={form.title} onChange={(e) => setForm({...form, title: e.target.value})} className="rounded-xl mt-1" />
                </div>
                <div>
                  <Label className="text-xs font-semibold text-slate-600">Content</Label>
                  <Textarea data-testid="ann-content" rows={5} value={form.content} onChange={(e) => setForm({...form, content: e.target.value})} className="rounded-xl mt-1" />
                </div>
                <div className="flex items-center gap-6">
                  <label className="flex items-center gap-2 text-sm">
                    <input type="checkbox" data-testid="ann-pinned" checked={form.is_pinned} onChange={(e) => setForm({...form, is_pinned: e.target.checked})} />
                    Pin to top
                  </label>
                  <label className="flex items-center gap-2 text-sm">
                    <input type="checkbox" data-testid="ann-high" checked={form.priority === 'high'} onChange={(e) => setForm({...form, priority: e.target.checked ? 'high' : 'normal'})} />
                    High priority
                  </label>
                </div>
              </div>
              <DialogFooter>
                <Button variant="outline" onClick={() => setOpen(false)} className="rounded-full">Cancel</Button>
                <Button onClick={submit} disabled={busy} data-testid="submit-announcement" className="rounded-full bg-[#3D4A8E] hover:bg-[#5C6BC0]">
                  {busy ? 'Posting…' : 'Post announcement'}
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        )}
      </div>

      <div className="space-y-4">
        {list.map(a => (
          <div key={a.id} data-testid={`announcement-${a.id}`}
               className={`border rounded-2xl p-6 ${a.is_pinned ? 'bg-[#3D4A8E]/[0.03] border-[#3D4A8E]/30' : 'bg-white border-slate-200'}`}>
            <div className="flex items-start justify-between gap-4">
              <div className="flex items-start gap-3 min-w-0">
                <div className={`w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0 ${a.priority === 'high' ? 'bg-red-50 text-red-600' : 'bg-[#3D4A8E]/10 text-[#3D4A8E]'}`}>
                  {a.priority === 'high' ? <AlertCircle className="w-5 h-5" /> : <Megaphone className="w-5 h-5" />}
                </div>
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2 flex-wrap">
                    <h3 className="font-heading text-lg font-semibold text-slate-900">{a.title}</h3>
                    {a.is_pinned && <span className="text-[10px] uppercase tracking-wider font-bold px-2 py-0.5 rounded-full bg-amber-50 text-amber-700 border border-amber-200 flex items-center gap-1"><Pin className="w-3 h-3" />Pinned</span>}
                    {a.priority === 'high' && <span className="text-[10px] uppercase tracking-wider font-bold px-2 py-0.5 rounded-full bg-red-50 text-red-700 border border-red-200">High</span>}
                  </div>
                  <p className="text-slate-600 text-sm mt-2 whitespace-pre-line leading-relaxed">{a.content}</p>
                  <div className="text-xs text-slate-400 mt-3">
                    Posted by <b>{a.posted_by_name}</b> · {new Date(a.created_at).toLocaleDateString()}
                  </div>
                </div>
              </div>
              {canPost && (
                <button onClick={() => del(a.id)} data-testid={`delete-ann-${a.id}`}
                        className="text-slate-400 hover:text-red-500 transition-colors">
                  <Trash2 className="w-4 h-4" />
                </button>
              )}
            </div>
          </div>
        ))}
        {list.length === 0 && (
          <div className="text-center py-16 text-slate-400 flex flex-col items-center gap-3">
            <Megaphone className="w-10 h-10 text-slate-300" />
            <div>No announcements yet.</div>
          </div>
        )}
      </div>
    </div>
  );
}
